import { io, Socket } from 'socket.io-client';
import { WS_URL, APP_TYPE } from '@soundbyte/config/constants';

/**
 * Socket Event Types
 */
export interface GenerationStatusEvent {
  generationId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress?: number;
  message?: string;
}

export interface GenerationProgressEvent {
  generationId: string;
  progress: number;
  stage: string;
}

export interface GenerationCompletedEvent {
  generationId: string;
  imageUrl: string;
  thumbnailUrl: string;
}

export interface NotificationEvent {
  id: string;
  type: string;
  title: string;
  message: string;
  data?: Record<string, unknown>;
  createdAt: string;
}

export interface RequestUpdateEvent {
  requestId: string;
  requestType: 'soundbyte' | 'sonic';
  status: string;
  message?: string;
}

type EventCallback<T> = (data: T) => void;

/**
 * Socket Service
 *
 * Manages WebSocket connection for real-time updates.
 * Uses Socket.IO for communication with the backend.
 */
class SocketService {
  private socket: Socket | null = null;
  private isConnecting = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private listeners: Map<string, Set<EventCallback<unknown>>> = new Map();

  /**
   * Connect to the WebSocket server
   */
  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.socket?.connected) {
        resolve();
        return;
      }

      if (this.isConnecting) {
        // Wait for existing connection attempt
        const checkConnection = setInterval(() => {
          if (this.socket?.connected) {
            clearInterval(checkConnection);
            resolve();
          }
        }, 100);
        return;
      }

      this.isConnecting = true;

      this.socket = io(WS_URL, {
        withCredentials: true, // Send cookies for session auth
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        extraHeaders: {
          'X-App-Type': APP_TYPE, // Identifies app for session isolation
        },
      });

      this.socket.on('connect', () => {
        // [Socket] Connected to server');
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        resolve();
      });

      this.socket.on('disconnect', () => {
        // Socket disconnected
      });

      this.socket.on('connect_error', () => {
        // Socket connection error
        this.isConnecting = false;
        this.reconnectAttempts++;

        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          reject(new Error('Failed to connect to WebSocket server'));
        }
      });

      // Re-register all listeners on reconnect
      this.socket.on('connect', () => {
        this.reregisterListeners();
      });

      // Set up default event handlers
      this.setupDefaultHandlers();
    });
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.listeners.clear();
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Set up default event handlers
   */
  private setupDefaultHandlers(): void {
    if (!this.socket) return;

    // Generation status updates
    this.socket.on('generation:status', (data: GenerationStatusEvent) => {
      this.emit('generation:status', data);
    });

    // Generation progress updates
    this.socket.on('generation:progress', (data: GenerationProgressEvent) => {
      this.emit('generation:progress', data);
    });

    // Generation completed
    this.socket.on('generation:completed', (data: GenerationCompletedEvent) => {
      this.emit('generation:completed', data);
    });

    // Notifications
    this.socket.on('notification', (data: NotificationEvent) => {
      this.emit('notification', data);
    });

    // Request updates (for Originals and Sonic)
    this.socket.on('request:updated', (data: RequestUpdateEvent) => {
      this.emit('request:updated', data);
    });
  }

  /**
   * Re-register listeners after reconnect
   */
  private reregisterListeners(): void {
    // Listeners are already set up via on() method
    // This is called to ensure any room subscriptions are renewed
  }

  /**
   * Emit event to local listeners
   */
  private emit(event: string, data: unknown): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach((callback) => callback(data));
    }
  }

  /**
   * Subscribe to generation status updates
   */
  onGenerationStatus(callback: EventCallback<GenerationStatusEvent>): () => void {
    return this.on('generation:status', callback);
  }

  /**
   * Subscribe to generation progress updates
   */
  onGenerationProgress(callback: EventCallback<GenerationProgressEvent>): () => void {
    return this.on('generation:progress', callback);
  }

  /**
   * Subscribe to generation completed events
   */
  onGenerationCompleted(callback: EventCallback<GenerationCompletedEvent>): () => void {
    return this.on('generation:completed', callback);
  }

  /**
   * Subscribe to notification events
   */
  onNotification(callback: EventCallback<NotificationEvent>): () => void {
    return this.on('notification', callback);
  }

  /**
   * Subscribe to request update events
   */
  onRequestUpdate(callback: EventCallback<RequestUpdateEvent>): () => void {
    return this.on('request:updated', callback);
  }

  /**
   * Generic event subscription
   */
  on<T>(event: string, callback: EventCallback<T>): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback as EventCallback<unknown>);

    // Return unsubscribe function
    return () => {
      const callbacks = this.listeners.get(event);
      if (callbacks) {
        callbacks.delete(callback as EventCallback<unknown>);
      }
    };
  }

  /**
   * Subscribe to a specific generation's updates
   */
  subscribeToGeneration(generationId: string): void {
    if (this.socket?.connected) {
      this.socket.emit('subscribe:generation', { generationId });
    }
  }

  /**
   * Unsubscribe from a specific generation's updates
   */
  unsubscribeFromGeneration(generationId: string): void {
    if (this.socket?.connected) {
      this.socket.emit('unsubscribe:generation', { generationId });
    }
  }

  /**
   * Subscribe to request updates
   */
  subscribeToRequest(requestId: string, requestType: 'soundbyte' | 'sonic'): void {
    if (this.socket?.connected) {
      this.socket.emit('subscribe:request', { requestId, requestType });
    }
  }

  /**
   * Unsubscribe from request updates
   */
  unsubscribeFromRequest(requestId: string): void {
    if (this.socket?.connected) {
      this.socket.emit('unsubscribe:request', { requestId });
    }
  }
}

export const socketService = new SocketService();
